import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';

export default function CustomerOrder() {
  const navigate               = useNavigate();
  const location                = useLocation();
  const { user }                = useAuth();
  const socket                  = useSocket();
  const [menu, setMenu]         = useState([]);
  const [balance, setBalance]   = useState(0);
  const [cart, setCart]         = useState({});
  const [loading, setLoading]   = useState(true);
  const [placing, setPlacing]   = useState(false);
  const [error, setError]       = useState('');
  const [showCart, setShowCart] = useState(false);
  const [detailItem, setDetailItem] = useState(null); // the menu item currently shown full-screen, or null
  const [detailImageIndex, setDetailImageIndex] = useState(0);
  // Safety nudge: if the device placing an order isn't near the delivery
  // address on file, the server hands back needsLocationConfirm instead of
  // placing it — this holds that prompt until the customer either confirms
  // it's still right or updates their address.
  const [locationPrompt, setLocationPrompt] = useState(null); // { distanceMiles, address, message }
  const [pendingCoords, setPendingCoords] = useState(null);
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ address: '', zip: '' });
  const [savingAddress, setSavingAddress] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  // Product type is the real top level of the menu — Flower/Edibles/Carts/
  // etc. A Dashboard tile arrives with one already chosen (passed via nav
  // state); landing here any other way (e.g. the bottom-nav Order icon)
  // starts with none selected, so the type picker below is the first thing
  // shown rather than dumping straight into an unfiltered item list.
  const [activeType, setActiveType] = useState(location.state?.productType || null);

  useEffect(() => { fetchMenu(); fetchBalance(); }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('menu:updated',   fetchMenu);
    socket.on('wallet:updated', ({ balance: b }) => setBalance(b));
    return () => { socket.off('menu:updated'); socket.off('wallet:updated'); };
  }, [socket]);

  async function fetchMenu() {
    try {
      const res = await axios.get('/api/menu');
      setMenu(res.data.menu || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function fetchBalance() {
    // First use what we already know from user context
    const localBalance = parseFloat(user?.wallet) || 0;
    setBalance(localBalance);
    // Then fetch fresh from server
    try {
      const res = await axios.get('/api/coins/balance');
      if (res.data.balance !== undefined) setBalance(res.data.balance);
    } catch(e) {
      // Keep local balance if server fails
      console.log('Using cached balance:', localBalance);
    }
  }

  const cartItems  = Object.values(cart);
  const cartTotal  = cartItems.reduce((s, i) => s + (i.totalCoins || 0), 0);
  const cartCount  = cartItems.reduce((s, i) => s + (i.quantity || 0), 0);
  const remaining  = balance - cartTotal;

  function updateCart(key, strainName, size, quantity, coinsEach) {
    setCart(prev => {
      if (quantity <= 0) { const next = {...prev}; delete next[key]; return next; }
      return { ...prev, [key]: { strainName, size, quantity, coinsEach, totalCoins: quantity * coinsEach } };
    });
  }

  function getCartItem(key) { return cart[key] || { quantity:0, totalCoins:0 }; }

  // Shared by the list card and the full-screen detail view, so the two
  // never drift out of sync — same add-to-cart controls either place.
  function renderPricingControls(item) {
    if (item.type === 'weight' && item.prices && item.prices.length > 0) {
      return (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {item.prices.map(price => {
            const key        = `${item.strain}-${price.size}`;
            const cartItem   = getCartItem(key);
            const currentQty = cartItem.quantity;
            const currentCost = cartItem.totalCoins;
            const available  = remaining + currentCost;
            const canAfford  = price.coins <= available;
            const maxQty     = Math.floor(available / price.coins);

            return (
              <div key={price.size} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'8px 12px', borderRadius:10,
                background: canAfford ? 'var(--bg-base)' : '#03050f',
                border:`1px solid ${currentQty > 0 ? 'var(--accent-blue)' : canAfford ? 'var(--border)' : '#0a0f1a'}`,
                opacity: canAfford ? 1 : 0.5
              }}>
                <div>
                  <p style={{ fontSize:13, color: canAfford ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: currentQty > 0 ? 600 : 400 }}>{price.size}</p>
                  <p style={{ fontSize:11, color: currentQty > 0 ? 'var(--accent-blue)' : 'var(--text-muted)' }}>
                    {price.coins} coins {!canAfford && currentQty === 0 && `• need ${price.coins - Math.floor(remaining)} more`}
                  </p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={() => updateCart(key, item.strain, price.size, currentQty - 1, price.coins)}
                    disabled={currentQty === 0}
                    style={{ width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--text-primary)', fontSize:18, cursor: currentQty===0?'not-allowed':'pointer', opacity: currentQty===0?0.3:1 }}>−</button>
                  <span style={{ fontSize:15, minWidth:20, textAlign:'center', color:'#fff', fontWeight:600 }}>{currentQty}</span>
                  <button onClick={() => { if(canAfford && currentQty < maxQty) updateCart(key, item.strain, price.size, currentQty+1, price.coins); }}
                    disabled={!canAfford || currentQty >= maxQty}
                    style={{ width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background: canAfford && currentQty < maxQty ? 'var(--accent-blue)' : 'var(--bg-card)', color:'#fff', fontSize:18, cursor:(!canAfford||currentQty>=maxQty)?'not-allowed':'pointer' }}>+</button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (item.type === 'gram' && item.prices?.[0]) {
      const price      = item.prices[0];
      const key        = `${item.strain}-gram`;
      const cartItem   = getCartItem(key);
      const currentQty = cartItem.quantity;
      const currentCost = cartItem.totalCoins;
      const available  = remaining + currentCost;
      const canAfford  = price.coins <= available;
      const maxGrams   = Math.floor(available / price.coins);
      return (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <p style={{ fontSize:12, color:'var(--text-secondary)' }}>{price.coins} coins/gram</p>
            <p style={{ fontSize:12, color:'var(--accent-blue)' }}>{currentQty}g = {currentQty * price.coins} coins</p>
          </div>
          {canAfford ? (
            <>
              <input type="range" min={0} max={Math.max(maxGrams, currentQty)} value={currentQty}
                onChange={e => updateCart(key, item.strain, 'gram', parseInt(e.target.value), price.coins)}
                style={{ width:'100%' }} />
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ fontSize:10, color:'var(--text-muted)' }}>0g</span>
                <span style={{ fontSize:10, color:'var(--text-muted)' }}>{maxGrams}g max</span>
              </div>
            </>
          ) : (
            <p style={{ fontSize:11, color:'var(--accent-red)' }}>Need {price.coins - Math.floor(remaining)} more coins</p>
          )}
        </div>
      );
    }

    return null;
  }

  // Never blocks placing an order on its own — denied/unavailable location
  // just resolves to no coordinates, same as the server treats a missing lat/lng.
  function getDeviceLocation() {
    return new Promise(resolve => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 8000, maximumAge: 60000 }
      );
    });
  }

  async function placeOrder() {
    const items = Object.values(cart);
    if (items.length === 0) { setError('Add at least one item.'); return; }
    if (cartTotal > balance) { setError('Insufficient coin balance.'); return; }
    setError(''); setPlacing(true);
    try {
      const coords = await getDeviceLocation();
      setPendingCoords(coords);
      const res = await axios.post('/api/orders', { items, totalCoins: cartTotal, lat: coords?.lat, lng: coords?.lng });
      if (res.data.needsLocationConfirm) { setLocationPrompt(res.data); return; }
      navigate('/track', { state: { oid: res.data.oid } });
    } catch(e) { setError(e.response?.data?.error || 'Failed to place order.'); }
    finally { setPlacing(false); }
  }

  // Customer says the address on file is right even though they're not
  // there right now — resubmits the same order, skipping the location check.
  async function confirmOrderAnyway() {
    const items = Object.values(cart);
    setPlacing(true);
    try {
      const res = await axios.post('/api/orders', { items, totalCoins: cartTotal, lat: pendingCoords?.lat, lng: pendingCoords?.lng, confirmLocation: true });
      setLocationPrompt(null);
      navigate('/track', { state: { oid: res.data.oid } });
    } catch(e) { setError(e.response?.data?.error || 'Failed to place order.'); }
    finally { setPlacing(false); }
  }

  async function saveAddressAndRetry() {
    if (!addressForm.address.trim() || !addressForm.zip.trim()) return;
    setSavingAddress(true);
    try {
      await axios.put(`/api/members/${user.mid}/address`, addressForm);
      setLocationPrompt(null);
      setEditingAddress(false);
      await placeOrder(); // re-checks location against the address just saved
    } catch(e) { alert(e.response?.data?.error || 'Failed to update address'); }
    finally { setSavingAddress(false); }
  }

  const catIcons    = { Cheapies:'🌿', Mids:'🍃', ZaZa:'⭐', Gas:'🔥' };
  const typeIcons   = { Flower:'🌿', Edibles:'🍬', Tinctures:'💧', Carts:'🔋', Concentrates:'🍯', Other:'📦' };
  const productTypes = [...new Set(menu.map(i => i.productType || 'Flower'))];
  const inType       = menu.filter(i => (i.productType || 'Flower') === activeType);
  const categories    = ['All', ...new Set(inType.map(i => i.category))];
  const filtered       = activeCategory === 'All' ? inType : inType.filter(i => i.category === activeCategory);

  if (!activeType) {
    return (
      <div className="app-shell">
        <div style={{ padding:'14px 20px 10px', borderBottom:'1px solid var(--border)' }}>
          <button className="back-btn" onClick={() => navigate('/dashboard')} style={{ marginBottom:4 }}>← Back</button>
          <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:20, letterSpacing:2 }}>What are you looking for?</h2>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
          {loading ? (
            <div className="loading">Loading menu...</div>
          ) : productTypes.length === 0 ? (
            <p style={{ color:'var(--text-secondary)', textAlign:'center', marginTop:40 }}>No products available.</p>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {productTypes.map(type => (
                <div key={type} onClick={() => setActiveType(type)}
                  style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:16, cursor:'pointer' }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>{typeIcons[type] || '📦'}</div>
                  <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:17, letterSpacing:1 }}>{type}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="nav-bar">
          <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
          <button className="nav-item active"><span style={{ fontSize:17 }}>🛒</span>Order</button>
          <button className="nav-item" onClick={() => navigate('/wallet')}><span style={{ fontSize:17 }}>💰</span>Wallet</button>
          <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
          <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{ padding:'14px 20px 10px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <button className="back-btn" onClick={() => { setActiveType(null); setActiveCategory('All'); }} style={{ marginBottom:4 }}>← Back</button>
          <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:20, letterSpacing:2 }}>{activeType}</h2>
        </div>
        <div style={{ textAlign:'right' }}>
          <p style={{ fontSize:10, color:'var(--text-secondary)' }}>Available</p>
          <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, color: remaining < 0 ? 'var(--accent-red)' : 'var(--accent-blue)' }}>
            {remaining.toFixed(0)} coins
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div style={{ display:'flex', gap:8, padding:'10px 20px', overflowX:'auto', borderBottom:'1px solid var(--border)' }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setActiveCategory(cat)} style={{
            padding:'6px 14px', borderRadius:20, border:'none', cursor:'pointer', fontSize:12, whiteSpace:'nowrap',
            fontFamily:'DM Sans, sans-serif',
            background: activeCategory === cat ? 'var(--accent-blue)' : 'var(--bg-card)',
            color: activeCategory === cat ? '#fff' : 'var(--text-secondary)'
          }}>{cat}</button>
        ))}
      </div>

      {/* Menu */}
      <div style={{ flex:1, overflowY:'auto', padding:'12px 20px', paddingBottom: cartCount > 0 ? 170 : 20 }}>
        {loading ? (
          <div className="loading">Loading menu...</div>
        ) : filtered.length === 0 ? (
          <p style={{ color:'var(--text-secondary)', textAlign:'center', marginTop:40 }}>No products available.</p>
        ) : (
          filtered.map(item => (
            <div key={item.strain} style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, marginBottom:12, overflow:'hidden' }}>
              {/* Product header — tap to see the full description/photos full-screen */}
              <div onClick={() => { setDetailItem(item); setDetailImageIndex(0); }}
                style={{ display:'flex', gap:12, alignItems:'center', padding:'12px 14px 10px', cursor:'pointer' }}>
                {/* Uploaded menu art is a wide banner (~2.5:1, logo/text centered across it),
                    so the thumbnail box matches that ratio instead of a square — a square
                    crop isolates whatever's dead-center, which is usually just the wordmark. */}
                <div style={{ width:84, height:34, borderRadius:8, flexShrink:0, background:'var(--bg-base)', border:'1px solid var(--border)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {item.imageUrl
                    ? <img src={item.imageUrl} alt={item.strain} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    : <span style={{ fontSize:20 }}>{typeIcons[item.productType] || catIcons[item.category] || '🌿'}</span>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:14, fontWeight:600, color:'var(--text-primary)' }}>{item.strain}</p>
                  <p style={{ fontSize:11, color:'var(--text-secondary)' }}>{item.productType || 'Flower'} • {item.category}</p>
                  {item.notes && <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.notes}</p>}
                </div>
                <span style={{ color:'var(--text-muted)', fontSize:18, flexShrink:0 }}>›</span>
              </div>

              <div style={{ padding:'0 14px 12px' }}>
                {renderPricingControls(item)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Floating Cart Button — sits above the nav bar below, not over it */}
      {cartCount > 0 && !showCart && (
        <div style={{ position:'fixed', bottom:86, left:20, right:20, zIndex:40 }}>
          <button onClick={() => setShowCart(true)} style={{
            width:'100%', padding:'14px 20px', background:'var(--accent-red)', color:'#fff',
            border:'none', borderRadius:14, fontSize:14, fontWeight:600, cursor:'pointer',
            fontFamily:'DM Sans, sans-serif', display:'flex', justifyContent:'space-between', alignItems:'center',
            boxShadow:'0 4px 20px #c0101a60'
          }}>
            <span>🛒 {cartCount} item{cartCount>1?'s':''} in cart</span>
            <span style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, letterSpacing:1 }}>{cartTotal} coins →</span>
          </button>
        </div>
      )}

      {/* Nav Bar */}
      <div className="nav-bar">
        <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
        <button className="nav-item active"><span style={{ fontSize:17 }}>🛒</span>Order</button>
        <button className="nav-item" onClick={() => navigate('/wallet')}><span style={{ fontSize:17 }}>💰</span>Wallet</button>
        <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
        <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
      </div>

      {/* Cart Drawer */}
      {showCart && (
        <div style={{ position:'fixed', inset:0, zIndex:50 }}>
          <div onClick={() => setShowCart(false)} style={{ position:'absolute', inset:0, background:'#000000aa' }} />
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'var(--bg-base)', borderRadius:'20px 20px 0 0', padding:'20px 20px 36px', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 16px' }} />
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:22, letterSpacing:2, marginBottom:16 }}>Your Cart</p>

            {cartItems.map((item, i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                <div>
                  <p style={{ fontSize:14, color:'var(--text-primary)' }}>{item.strainName}</p>
                  <p style={{ fontSize:12, color:'var(--text-secondary)' }}>{item.size} × {item.quantity}</p>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:16, color:'var(--accent-blue)' }}>{item.totalCoins}c</p>
                  <button onClick={() => {
                    const key = `${item.strainName}-${item.size === 'gram' ? 'gram' : item.size}`;
                    updateCart(key, item.strainName, item.size, 0, item.coinsEach);
                  }} style={{ background:'none', border:'none', color:'var(--accent-red)', fontSize:18, cursor:'pointer' }}>×</button>
                </div>
              </div>
            ))}

            <div style={{ display:'flex', justifyContent:'space-between', padding:'14px 0', marginTop:4 }}>
              <span style={{ fontSize:14, color:'var(--text-secondary)' }}>Total</span>
              <span style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:22, color:'var(--accent-blue)' }}>{cartTotal} coins</span>
            </div>

            {error && <p style={{ color:'var(--accent-red)', fontSize:12, marginBottom:10 }}>{error}</p>}

            <button className="btn-primary" onClick={placeOrder} disabled={placing || cartTotal === 0}>
              {placing ? 'Placing Order...' : 'Place Order'}
            </button>
            <button onClick={() => setShowCart(false)} style={{ display:'block', width:'100%', marginTop:10, padding:12, background:'none', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-secondary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              Continue Shopping
            </button>
            <p style={{ textAlign:'center', fontSize:11, color:'var(--text-muted)', marginTop:10 }}>
              Coins deduct only when your order is delivered
            </p>
          </div>
        </div>
      )}

      {/* Not-at-your-address safety check */}
      {locationPrompt && (
        <div style={{ position:'fixed', inset:0, zIndex:60 }}>
          <div style={{ position:'absolute', inset:0, background:'#000000cc' }} />
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'var(--bg-base)', borderRadius:'20px 20px 0 0', padding:'24px 20px 36px' }}>
            <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 16px' }} />
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:20, letterSpacing:1, marginBottom:10 }}>
              You're not near your delivery address
            </p>
            <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:20 }}>
              {locationPrompt.message}
            </p>

            {!editingAddress ? (
              <>
                <button className="btn-primary" onClick={confirmOrderAnyway} disabled={placing}>
                  {placing ? 'Placing Order...' : 'Yes, still deliver there'}
                </button>
                <button onClick={() => {
                  setAddressForm({ address: user?.address || '', zip: user?.zip || '' });
                  setEditingAddress(true);
                }} style={{ display:'block', width:'100%', marginTop:10, padding:12, background:'none', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-secondary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Update my address instead
                </button>
                <button onClick={() => setLocationPrompt(null)} style={{ display:'block', width:'100%', marginTop:10, padding:10, background:'none', border:'none', color:'var(--text-muted)', fontSize:12, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <div className="field">
                  <label>Delivery Address</label>
                  <input type="text" value={addressForm.address} onChange={e => setAddressForm(f => ({ ...f, address: e.target.value }))} required />
                </div>
                <div className="field">
                  <label>Zip Code</label>
                  <input type="text" value={addressForm.zip} onChange={e => setAddressForm(f => ({ ...f, zip: e.target.value }))} required />
                </div>
                <button className="btn-primary" onClick={saveAddressAndRetry} disabled={savingAddress || !addressForm.address.trim() || !addressForm.zip.trim()}>
                  {savingAddress ? 'Saving...' : 'Save Address & Place Order'}
                </button>
                <button onClick={() => setEditingAddress(false)} style={{ display:'block', width:'100%', marginTop:10, padding:12, background:'none', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-secondary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
                  Back
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full-screen product detail — tapped from the menu list, shows the
          complete description and every photo (up to 4), same add-to-cart
          controls as the list card so adjusting quantity here and there
          never disagree. */}
      {detailItem && (
        <div style={{ position:'fixed', inset:0, zIndex:70, background:'var(--bg-base)', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'14px 20px 10px', borderBottom:'1px solid var(--border)' }}>
            <button className="back-btn" onClick={() => setDetailItem(null)}>← Back to Menu</button>
          </div>

          <div style={{ flex:1, overflowY:'auto' }}>
            {/* objectFit:'contain' (not 'cover') — the uploaded art is a wide ~2.5:1
                banner, and a square box with 'cover' only shows the center 40% of it,
                cropping off whatever text sits near the left/right edges. 'contain'
                guarantees the whole image is visible, letterboxed, no matter its shape. */}
            <div style={{ width:'100%', aspectRatio:'1', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
              {detailItem.images && detailItem.images.length > 0
                ? <img src={detailItem.images[detailImageIndex] || detailItem.images[0]} alt={detailItem.strain} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                : <span style={{ fontSize:64 }}>{typeIcons[detailItem.productType] || catIcons[detailItem.category] || '🌿'}</span>}
            </div>

            {detailItem.images && detailItem.images.length > 1 && (
              <div style={{ display:'flex', gap:8, padding:'10px 20px', overflowX:'auto' }}>
                {detailItem.images.map((img, i) => (
                  <button key={i} onClick={() => setDetailImageIndex(i)} style={{
                    width:56, height:56, borderRadius:8, overflow:'hidden', padding:0, cursor:'pointer', flexShrink:0,
                    border: i === detailImageIndex ? '2px solid var(--accent-blue)' : '1px solid var(--border)'
                  }}>
                    <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                  </button>
                ))}
              </div>
            )}

            <div style={{ padding:'16px 20px 24px' }}>
              <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:26, letterSpacing:1, marginBottom:4 }}>{detailItem.strain}</p>
              <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:16 }}>{detailItem.productType || 'Flower'} • {detailItem.category}</p>
              {detailItem.notes && <p style={{ fontSize:14, color:'var(--text-primary)', lineHeight:1.6, marginBottom:20 }}>{detailItem.notes}</p>}

              {renderPricingControls(detailItem)}
            </div>
          </div>

          <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)' }}>
            <button className="btn-primary" onClick={() => setDetailItem(null)}>Done — Back to Menu</button>
          </div>

          <div className="nav-bar">
            <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
            <button className="nav-item active"><span style={{ fontSize:17 }}>🛒</span>Order</button>
            <button className="nav-item" onClick={() => navigate('/wallet')}><span style={{ fontSize:17 }}>💰</span>Wallet</button>
            <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
            <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
          </div>
        </div>
      )}
    </div>
  );
}
